import {useMemo} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {unlinkDocument, unlinkLinkedResponse, updateDocument} from '../../../actions/Document/documentactions';
import {raiseDochelperError} from '../../../actions/erroractions';
import {getAllRelatedDocuments} from '../../../actions/FormBodyRelatedDocuments/formbodyrelateddocumentsactions';
import {deleteRelatedDocument} from '../../../actions/RelatedDocuments/relatedDocumentsActions';
import {downloadDocumentWithDocumentHelper} from '../../../actions/Task/documenthubactions';
import {
	deleteUserPresence,
	getUserPresence,
	resetUserPresence
} from '../../../actions/UserPresence/userpresenceactions';
import {useUserPreferences} from '../../../util/customHooks';
import {labels, currentResource} from '../../../util/utils';
import Utility from '../../../util/utilityfunctions';
import {confidentialityTypes} from '../../../util/uiconstants';

export const useRenameDocumentErrors = () => {
	return useMemo(() => {
		return {
			maxLengthExceeded: labels.maxLengthExceeded,
			invalidCharacters: labels.invalidCharacters,
			fileNameRequired: labels.fileNameRequired
		};
	}, []);
};

export const useDocumentUploadStatus = (enableOpenDocument, documentId, documentName, fileExt, isDragAndDrop) => {
	let documentHelperKey;
	if (isDragAndDrop) {
		documentHelperKey = Utility.getDocumentHelperKey(documentId.toLowerCase(), documentName, fileExt);
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const {progressPercent, inProgress} = useSelector(
			(state) =>
				state.uploadDocuments[documentHelperKey] || {
					inProgress: false
				},
			shallowEqual
		);

		return {progressPercent, inProgress};
	} else {
		if (enableOpenDocument && documentId) {
			if (documentId.indexOf(documentName) >= 0) {
				/*document upload is in progress*/
				documentHelperKey = documentId;
			} else {
				documentHelperKey = Utility.getDocumentHelperKey(documentId.toLowerCase(), documentName, fileExt);
			}
		}

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const {documentHubStatus, progressPercent, inProgress} = useSelector(
			(state) =>
				state.docHelperDocuments[documentHelperKey] || {
					inProgress: false
				},
			shallowEqual
		);

		return {documentHubStatus, progressPercent, inProgress};
	}
};

export const useConfidentialityConfiguration = () => {
	const userPreferences = useUserPreferences();
	const confidentialityConfiguration = {
		confidentialityLabel: labels.confidentiality,
		confidentialityDefaultLabel: currentResource.confidentialityLevels[confidentialityTypes.DEFAULT],
		confidentialityLowLabel: currentResource.confidentialityLevels[confidentialityTypes.LOW],
		confidentialityModerateLabel: currentResource.confidentialityLevels[confidentialityTypes.MODERATE],
		confidentialityHighLabel: currentResource.confidentialityLevels[confidentialityTypes.HIGH],
		confidentialityConfidentialLabel: currentResource.confidentialityLevels[confidentialityTypes.CONFIDENTIAL],
		confidentialityNotOpenableLabel: labels.confidentialityNotOpenable,
		confidentialityTargetNotOpenable: labels.confidentialityTargetNotOpenable,
		userConfidentialityTypeId: userPreferences.userConfidentialityTypeId
	};

	return confidentialityConfiguration;
};

export const useActions = () => {
	const dispatch = useDispatch();

	return {
		downloadDocumentWithDocumentHelper: (url, data, headers) =>
			dispatch(downloadDocumentWithDocumentHelper(url, data, headers)),
		raiseDochelperError: (error) => dispatch(raiseDochelperError(error)),
		updateDocument: (
			engagementId,
			documentId,
			data,
			resolvedVersionNumber,
			currentVersionNumber,
			isHistoryResolve,
			entityUId,
			viewKey
		) =>
			dispatch(
				updateDocument(
					engagementId,
					documentId,
					data,
					resolvedVersionNumber,
					currentVersionNumber,
					isHistoryResolve,
					entityUId,
					viewKey
				)
			),
		deleteRelatedDocument: (engagementID, documentId, bodyId, relatedDocumentId, comingFrom) =>
			dispatch(deleteRelatedDocument(engagementID, documentId, bodyId, relatedDocumentId, comingFrom)),
		getAllRelatedDocuments: (
			engagementId,
			targetEngagementId,
			documentId,
			entityUID,
			bodyId,
			filteredEntityType,
			categoryName,
			includeApprovals,
			pageSize,
			currentPage,
			searchString,
			includeReviewNotes
		) =>
			dispatch(
				getAllRelatedDocuments(
					engagementId,
					targetEngagementId,
					documentId,
					entityUID,
					bodyId,
					filteredEntityType,
					categoryName,
					includeApprovals,
					pageSize,
					currentPage,
					searchString,
					includeReviewNotes
				)
			),
		getUserPresence: (engagementId, objectId, entityId, objectActionTypeId) =>
			dispatch(getUserPresence(engagementId, objectId, entityId, objectActionTypeId)),
		deleteUserPresence: (engagementId, objectId, entityId, objectActionTypeId) =>
			dispatch(deleteUserPresence(engagementId, objectId, entityId, objectActionTypeId)),
		resetUserPresence: (objectId) => dispatch(resetUserPresence(objectId)),
		unlinkDocument: (engagementId, documentId, entityUId, viewKey) => dispatch(unlinkDocument(engagementId, documentId, entityUId, viewKey))
	};
};
